"use client";

import React, { useEffect, useRef, useState } from "react";
import { BiLoaderAlt } from "react-icons/bi";
import { FaPause, FaPlay } from "react-icons/fa";

import { cx } from "@/lib/tailwind-utils";

import Button from "./button";

// Global audio state management
const globalAudioState = new Map<
  string,
  {
    isPlaying: boolean;
    currentTime: number;
    audio?: HTMLAudioElement;
    isLoading?: boolean;
  }
>();

// Track currently loading audio
let currentlyLoadingAudioId: string | null = null;

interface PlayAudioProps {
  audioPath: string;
  id: string;
  getAudioURL: (path: string) => Promise<string | null>;
  className?: string;
}

export const PlayAudio: React.FC<PlayAudioProps> = ({
  audioPath,
  id,
  getAudioURL,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [playingAudio, setPlayingAudio] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize or get existing state
  useEffect(() => {
    const state = globalAudioState.get(id);
    if (state) {
      setPlayingAudio(state.isPlaying);
      setCurrentTime(state.currentTime);
      setIsLoading(state.isLoading || false);
      if (state.audio) {
        audioRef.current = state.audio;
        setDuration(state.audio.duration);
        if (state.isPlaying) {
          setIsExpanded(true);
        }
      }
    }

    // Listen for state changes from other instances
    const handleStateChange = (e: CustomEvent) => {
      if (e.detail.id === id) {
        const {
          isPlaying,
          currentTime,
          duration,
          isLoading: newIsLoading,
          audio: newAudio,
        } = e.detail;
        setPlayingAudio(isPlaying);
        setCurrentTime(currentTime);
        if (duration) setDuration(duration);
        setIsExpanded(isPlaying);
        if (typeof newIsLoading === "boolean") {
          setIsLoading(newIsLoading);
        }
        // Update audio reference if provided
        if (newAudio && newAudio !== audioRef.current) {
          audioRef.current = newAudio;
        }
      }
    };

    document.addEventListener(
      "audioStateChange",
      handleStateChange as EventListener,
    );
    return () => {
      document.removeEventListener(
        "audioStateChange",
        handleStateChange as EventListener,
      );
    };
  }, [id]);

  const updateAllInstances = (state: {
    isPlaying: boolean;
    currentTime: number;
    duration?: number;
    isLoading?: boolean;
    audio?: HTMLAudioElement;
  }) => {
    // Dispatch event to update all instances
    document.dispatchEvent(
      new CustomEvent("audioStateChange", {
        detail: {
          id,
          ...state,
        },
      }),
    );
  };

  const handleTimeUpdate = (e: Event) => {
    if (!isDragging && e.target instanceof HTMLAudioElement) {
      const newTime = e.target.currentTime;
      setCurrentTime(newTime);
      // Update global state
      const state = {
        isPlaying: !e.target.paused,
        currentTime: newTime,
        audio: e.target,
      };
      globalAudioState.set(id, state);
      updateAllInstances({
        isPlaying: !e.target.paused,
        currentTime: newTime,
        audio: e.target,
      });
    }
  };

  const handleLoadedMetadata = (e: Event) => {
    if (e.target instanceof HTMLAudioElement) {
      const newDuration = e.target.duration;
      setDuration(newDuration);
      updateAllInstances({
        isPlaying: !e.target.paused,
        currentTime: e.target.currentTime,
        duration: newDuration,
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    setCurrentTime(time);

    // Get the current audio from global state if not available in ref
    const audio = audioRef.current || globalAudioState.get(id)?.audio;

    if (audio) {
      audio.currentTime = time;
      audioRef.current = audio; // Ensure local ref is updated
      updateAllInstances({
        isPlaying: !audio.paused,
        currentTime: time,
        audio: audio,
      });
      if (playingAudio) {
        audio.play();
      }
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const stopAllOtherAudio = () => {
    // Stop all audio elements and update their state
    globalAudioState.forEach((state, audioId) => {
      if (audioId !== id && state.audio && !state.audio.paused) {
        state.audio.pause();
        globalAudioState.set(audioId, {
          ...state,
          isPlaying: false,
        });
        const otherPlayer = document.querySelector(
          `[data-audio-id="${audioId}"]`,
        );
        if (otherPlayer) {
          otherPlayer.dispatchEvent(
            new CustomEvent("audioStop", { bubbles: true }),
          );
        }
      }
    });
  };

  const togglePlayPause = async () => {
    // Get the current audio from global state if not available in ref
    const audio = audioRef.current || globalAudioState.get(id)?.audio;

    try {
      if (playingAudio && audio) {
        audio.pause();
        setPlayingAudio(false);
        setIsExpanded(false);
        const state = {
          isPlaying: false,
          currentTime: audio.currentTime,
          audio: audio,
          isLoading: false,
        };
        globalAudioState.set(id, state);
        updateAllInstances({
          isPlaying: false,
          currentTime: audio.currentTime,
          audio: audio,
        });
      } else {
        // Check if another audio is currently loading
        if (currentlyLoadingAudioId && currentlyLoadingAudioId !== id) {
          return; // Don't start loading if another audio is already loading
        }

        // Stop all other playing audio before starting new one
        stopAllOtherAudio();

        setIsExpanded(true);
        if (!audio || !audio.src) {
          setIsLoading(true);
          currentlyLoadingAudioId = id;
          const state = {
            isPlaying: false,
            currentTime: 0,
            isLoading: true,
          };
          globalAudioState.set(id, state);
          updateAllInstances({
            isPlaying: false,
            currentTime: 0,
            isLoading: true,
          });

          const url = await getAudioURL(audioPath);
          if (!url) {
            console.error("Failed to get audio URL");
            setIsLoading(false);
            currentlyLoadingAudioId = null;
            globalAudioState.set(id, { ...state, isLoading: false });
            updateAllInstances({
              isPlaying: false,
              currentTime: 0,
              isLoading: false,
            });
            return;
          }
          const newAudio = new Audio(url);
          newAudio.id = `callAudio-${id}`;
          audioRef.current = newAudio;
          newAudio.onended = () => {
            setPlayingAudio(false);
            const state = {
              isPlaying: false,
              currentTime: 0,
              audio: newAudio,
              isLoading: false,
            };
            globalAudioState.set(id, state);
            updateAllInstances({ isPlaying: false, currentTime: 0 });
            URL.revokeObjectURL(newAudio.src);
          };
          newAudio.addEventListener("timeupdate", handleTimeUpdate);
          newAudio.addEventListener("loadedmetadata", handleLoadedMetadata);
          const playPromise = newAudio.play();
          if (playPromise !== undefined) {
            await playPromise;
            setPlayingAudio(true);
            setIsLoading(false);
            currentlyLoadingAudioId = null;
            const state = {
              isPlaying: true,
              currentTime: newAudio.currentTime,
              audio: newAudio,
              isLoading: false,
            };
            globalAudioState.set(id, state);
            updateAllInstances({
              isPlaying: true,
              currentTime: newAudio.currentTime,
              duration: newAudio.duration,
              isLoading: false,
            });
          }
        } else {
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            await playPromise;
            setPlayingAudio(true);
            const state = {
              isPlaying: true,
              currentTime: audio.currentTime,
              audio: audio,
              isLoading: false,
            };
            globalAudioState.set(id, state);
            updateAllInstances({
              isPlaying: true,
              currentTime: audio.currentTime,
              duration: audio.duration,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error playing audio:", error);
      setPlayingAudio(false);
      setIsExpanded(false);
      setIsLoading(false);
      currentlyLoadingAudioId = null;
      const state = {
        isPlaying: false,
        currentTime: 0,
        audio: audioRef.current || undefined,
        isLoading: false,
      };
      globalAudioState.set(id, state);
      updateAllInstances({
        isPlaying: false,
        currentTime: 0,
        isLoading: false,
      });
    }
  };

  useEffect(() => {
    const handleAudioStop = () => {
      setPlayingAudio(false);
      setIsExpanded(false);
      if (audioRef.current) {
        globalAudioState.set(id, {
          isPlaying: false,
          currentTime: audioRef.current.currentTime,
          audio: audioRef.current,
        });
      }
    };

    document.addEventListener("audioStop", handleAudioStop);
    return () => document.removeEventListener("audioStop", handleAudioStop);
  }, [id]);

  return (
    <div
      className={cx("inline-flex flex-col items-center gap-2", className)}
      data-audio-id={id}
    >
      <div className="flex w-full min-w-[8rem] max-w-[14rem] items-center gap-2">
        <Button
          variant="secondary"
          onClick={togglePlayPause}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors"
          aria-label={isLoading ? "Loading" : playingAudio ? "Pause" : "Play"}
          disabled={isLoading}
        >
          {isLoading ? (
            <BiLoaderAlt size={16} className="animate-spin" />
          ) : playingAudio ? (
            <FaPause size={16} />
          ) : (
            <FaPlay size={16} className="ml-0.5" />
          )}
        </Button>

        {(isExpanded || isLoading) && (
          <div className="flex w-[calc(100%-2.5rem)] flex-col">
            <div className="group relative h-1 flex-grow">
              {/* Background track */}
              <div className="absolute h-full w-full rounded-full bg-muted" />

              {/* Progress bar */}
              <div
                className={`pointer-events-none absolute h-full rounded-full transition-none ${
                  isLoading
                    ? "animate-pulse bg-foreground-muted"
                    : "bg-foreground"
                }`}
                style={{
                  width: isLoading
                    ? "100%"
                    : `${(currentTime / duration) * 100 || 0}%`,
                }}
              />

              {/* Thumb */}
              <input
                type="range"
                min={0}
                max={duration}
                value={currentTime}
                step="any"
                onChange={handleSeek}
                onMouseDown={handleDragStart}
                onMouseUp={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchEnd={handleDragEnd}
                disabled={isLoading}
                className="absolute z-10 h-full w-full cursor-pointer appearance-none bg-transparent hover:cursor-pointer
                       [&::-moz-range-thumb]:h-2 [&::-moz-range-thumb]:w-2 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-foreground [&::-moz-range-thumb]:opacity-0 [&::-moz-range-thumb]:transition-opacity
                       group-hover:[&::-moz-range-thumb]:opacity-100 [&::-webkit-slider-thumb]:h-2 [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:opacity-0 [&::-webkit-slider-thumb]:transition-opacity group-hover:[&::-webkit-slider-thumb]:opacity-100"
                aria-label="Audio progress"
              />
            </div>

            <span className="mt-1 text-center text-caption-strong text-foreground-secondary">
              {isLoading
                ? "Loading audio..."
                : `${formatTime(currentTime)} / ${formatTime(duration)}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
